import api from '@/lib/api'
import type { PaginatedResponse } from '@/types'

// ── Types ──────────────────────────────────────────────────────────────────────

export interface BillingPlan {
  id: string
  name: string
  billing_cycle: 'monthly' | 'quarterly' | 'biannual' | 'annual'
  unit_type: 'vehicle' | 'employee' | 'student' | 'pet'
  price_per_unit: number
  currency: string
  is_active: boolean
  created_at: string
  /** Count of non-cancelled domain subscriptions using this plan (super admin only) */
  subscription_count?: number | null
}

export interface PlanDomainRow {
  domain_id: string
  domain_name: string
  domain_hostname: string
  subscription_id: string
  status: string
  unit_count: number
  current_period_start: string
  current_period_end: string
  next_billing_at: string | null
}

export interface DomainSubscription {
  id: string
  domain_id: string
  plan_id: string
  plan: BillingPlan | null
  status: 'active' | 'paused' | 'suspended' | 'cancelled'
  unit_count: number
  current_period_start: string
  current_period_end: string
  next_billing_at: string | null
  razorpay_subscription_id: string | null
  created_at: string
  updated_at: string
}

export interface CustomerSubscription {
  id: string
  domain_id: string
  customer_user_id: string
  plan_id: string
  plan: BillingPlan | null
  status: 'active' | 'paused' | 'suspended' | 'cancelled'
  unit_count: number
  /** Day-of-month (1–28) the subscription is anchored to; null = cycle from activation */
  billing_anchor_day: number | null
  current_period_start: string
  current_period_end: string
  next_billing_at: string | null
  razorpay_subscription_id: string | null
  created_at: string
  updated_at: string
}

export interface SubscriptionEntity {
  id: string
  subscription_id: string
  entity_type: 'vehicle' | 'employee' | 'student' | 'pet'
  entity_id: string
  is_suspended: boolean
  suspended_at: string | null
  suspended_reason: string | null
  restored_at: string | null
  created_at: string
  updated_at: string
}

export interface Invoice {
  id: string
  domain_id: string
  invoice_number: string
  /** PRO-XXXXXX number shown until confirmed to tax invoice */
  proforma_number: string | null
  invoice_type: 'proforma' | 'tax' | 'credit_note' | 'manual' | null
  subscription_id: string | null
  subscription_type: 'platform' | 'domain' | null
  customer_user_id: string | null
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'void'
  amount: number
  currency: string
  due_date: string | null
  paid_at: string | null
  notes: string | null
  tax_breakdown: { label: string; rate: number; amount: number }[] | null
  /** Dunning — populated when reminder emails are sent */
  reminder_1_sent_at: string | null
  reminder_2_sent_at: string | null
  created_at: string
  updated_at: string
}

export interface TaxConfig {
  default_currency: string
  tax_name: string | null
  tax_rate: number | null
  tax_number: string | null
  tax_number_label: string | null
  invoice_prefix: string | null
  fy_start_month: number | null
  invoice_include_fy: boolean
}

export interface Payment {
  id: string
  domain_id: string
  invoice_id: string | null
  payment_type: 'invoice' | 'wallet_topup'
  amount: number
  currency: string
  gateway: string
  gateway_payment_id: string | null
  gateway_order_id: string | null
  status: 'pending' | 'captured' | 'failed' | 'refunded'
  created_at: string
  updated_at: string
}

export interface WalletBalance {
  domain_id: string
  user_id: string | null
  balance: number
  currency: string
}

export interface WalletEntry {
  id: string
  domain_id: string
  user_id: string | null
  entry_type: 'credit' | 'debit'
  amount: number
  currency: string
  reference_type: string | null
  balance_after: number
  description: string | null
  created_at: string
}

export interface PaymentGatewayConfig {
  id: string
  domain_id: string | null
  gateway: string
  label: string | null
  is_active: boolean
  config: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface BillingProfile {
  id: string
  domain_id: string | null
  user_id: string | null
  legal_name: string
  business_type: 'individual' | 'business'
  tax_id_type: string | null
  tax_id: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  country: string | null
  postal_code: string | null
  invoice_email: string | null
  currency: string
  extra: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

// ── Platform Plans ─────────────────────────────────────────────────────────────

export const listBillingPlans = () =>
  api.get<{ success: true; data: BillingPlan[] }>('/billing/plans').then((r) => r.data.data)

export const createBillingPlan = (d: Omit<BillingPlan, 'id' | 'created_at'>) =>
  api.post<{ success: true; data: BillingPlan }>('/billing/plans', d).then((r) => r.data.data)

export const updateBillingPlan = (id: string, d: { name?: string; price_per_unit?: number; currency?: string; is_active?: boolean }) =>
  api.put<{ success: true; data: BillingPlan }>(`/billing/plans/${id}`, d).then((r) => r.data.data)

export const deleteBillingPlan = (id: string) =>
  api.delete(`/billing/plans/${id}`).then((r) => r.data)

/** List domains subscribed to a platform plan. Super admin only. */
export const getPlanDomains = (planId: string, status?: string) =>
  api
    .get<{ success: true; data: PlanDomainRow[]; meta: { total: number } }>(
      `/billing/plans/${planId}/domains`,
      { params: status ? { status } : undefined },
    )
    .then((r) => r.data)

// ── Domain Plans ───────────────────────────────────────────────────────────────

export const listDomainPlans = () =>
  api.get<{ success: true; data: BillingPlan[] }>('/billing/domain-plans').then((r) => r.data.data)

export const createDomainPlan = (d: Omit<BillingPlan, 'id' | 'created_at'>) =>
  api.post<{ success: true; data: BillingPlan }>('/billing/domain-plans', d).then((r) => r.data.data)

export const updateDomainPlan = (id: string, d: { name?: string; price_per_unit?: number; currency?: string; is_active?: boolean }) =>
  api.put<{ success: true; data: BillingPlan }>(`/billing/domain-plans/${id}`, d).then((r) => r.data.data)

export const deleteDomainPlan = (id: string) =>
  api.delete(`/billing/domain-plans/${id}`).then((r) => r.data)

// ── Domain Subscription ────────────────────────────────────────────────────────

export const getSubscription = () =>
  api.get<{ success: true; data: DomainSubscription }>('/billing/subscription').then((r) => r.data.data)

export const activateSubscription = (body: {
  plan_id: string
  unit_count?: number
  payment_method?: 'wallet' | 'gateway'
  gateway_order_id?: string
}) =>
  api.post<{ success: true; data: DomainSubscription }>('/billing/subscription/activate', body).then((r) => r.data.data)

export const cancelSubscription = (reason?: string) =>
  api.post('/billing/subscription/cancel', { reason }).then((r) => r.data)

export const changeSubscriptionPlan = (plan_id: string) =>
  api.put<{ success: true; data: DomainSubscription }>('/billing/subscription/plan', { plan_id }).then((r) => r.data.data)

export const pauseSubscription = () =>
  api.post<{ success: true; data: DomainSubscription }>('/billing/subscription/pause').then((r) => r.data.data)

export const resumeSubscription = () =>
  api.post<{ success: true; data: DomainSubscription }>('/billing/subscription/resume').then((r) => r.data.data)

// ── Customer Subscriptions ─────────────────────────────────────────────────────

export const getCustomerSubscription = (userId: string) =>
  api
    .get<{ success: true; data: CustomerSubscription[] }>(`/billing/customers/${userId}/subscription`)
    .then((r) => r.data.data ?? [])

export const createCustomerSubscription = (
  userId: string,
  body: { plan_id: string; billing_anchor_day?: number | null },
) =>
  api
    .post<{ success: true; data: CustomerSubscription }>(
      `/billing/customers/${userId}/subscription`,
      body,
    )
    .then((r) => r.data.data)

export const cancelCustomerSubscriptionById = (userId: string, subId: string) =>
  api.delete(`/billing/customers/${userId}/subscription/${subId}`).then((r) => r.data)

export const changeCustomerSubscriptionPlan = (userId: string, plan_id: string) =>
  api.put<{ success: true; data: CustomerSubscription }>(`/billing/customers/${userId}/subscription/plan`, { plan_id }).then((r) => r.data.data)

export const pauseCustomerSubscription = (userId: string) =>
  api.post<{ success: true; data: CustomerSubscription }>(`/billing/customers/${userId}/subscription/pause`).then((r) => r.data.data)

export const resumeCustomerSubscription = (userId: string) =>
  api.post<{ success: true; data: CustomerSubscription }>(`/billing/customers/${userId}/subscription/resume`).then((r) => r.data.data)

// ── Subscription Entities ──────────────────────────────────────────────────────

export const listSubscriptionEntities = (userId: string, subId: string) =>
  api
    .get<{ success: true; data: SubscriptionEntity[] | null }>(
      `/billing/customers/${userId}/subscription/${subId}/entities`,
    )
    .then((r) => r.data.data ?? [])

export const addSubscriptionEntity = (
  userId: string,
  subId: string,
  body: { entity_type: SubscriptionEntity['entity_type']; entity_id: string },
) =>
  api
    .post<{ success: true; data: SubscriptionEntity }>(
      `/billing/customers/${userId}/subscription/${subId}/entities`,
      body,
    )
    .then((r) => r.data.data)

export const removeSubscriptionEntity = (userId: string, subId: string, entityId: string) =>
  api
    .delete(`/billing/customers/${userId}/subscription/${subId}/entities/${entityId}`)
    .then((r) => r.data)

export const suspendSubscriptionEntity = (userId: string, subId: string, entityId: string, reason?: string) =>
  api
    .post(
      `/billing/customers/${userId}/subscription/${subId}/entities/${entityId}/suspend`,
      { reason },
    )
    .then((r) => r.data)

export const restoreSubscriptionEntity = (userId: string, subId: string, entityId: string) =>
  api
    .post(`/billing/customers/${userId}/subscription/${subId}/entities/${entityId}/restore`)
    .then((r) => r.data)

// ── Invoices ───────────────────────────────────────────────────────────────────

export const listInvoices = (p?: {
  status?: Invoice['status']
  page?: number
  limit?: number
}) =>
  api.get<PaginatedResponse<Invoice>>('/billing/invoices', { params: p }).then((r) => r.data)

export const getInvoice = (id: string) =>
  api
    .get<{ success: true; data: Invoice }>(`/billing/invoices/${id}`)
    .then((r) => r.data.data)

export const cancelInvoice = (id: string, reason?: string) =>
  api.post(`/billing/invoices/${id}/cancel`, { reason }).then((r) => r.data)

/** Download PDF — returns a blob URL */
export const getInvoicePdfUrl = (id: string) =>
  `${api.defaults.baseURL}/billing/invoices/${id}/pdf`

/** Record a manual payment against an invoice */
export const recordManualPayment = (id: string, body: { amount: number; currency: string; notes?: string; paid_at?: string }) =>
  api.post<{ success: true; data: Invoice }>(`/billing/invoices/${id}/payment`, body).then((r) => r.data.data)

/** Confirm a proforma → generates the real tax invoice */
export const confirmProforma = (id: string) =>
  api.post<{ success: true; data: Invoice }>(`/billing/invoices/${id}/confirm`).then((r) => r.data.data)

/** Issue a credit note against an invoice */
export const issueCreditNote = (id: string, body: { amount: number; reason?: string }) =>
  api.post<{ success: true; data: Invoice }>(`/billing/invoices/${id}/credit-note`, body).then((r) => r.data.data)

/** Create a free-text (manual) invoice */
export const createManualInvoice = (body: {
  customer_user_id?: string
  description: string
  amount: number
  currency: string
  due_date?: string
  notes?: string
}) =>
  api.post<{ success: true; data: Invoice }>('/billing/invoices/manual', body).then((r) => r.data.data)

// ── Payments ───────────────────────────────────────────────────────────────────

export const listPayments = (p?: { page?: number; per_page?: number }) =>
  api.get<PaginatedResponse<Payment>>('/billing/payments', { params: p }).then((r) => r.data)

// ── Wallet ─────────────────────────────────────────────────────────────────────

export const getWalletBalance = () =>
  api.get<{ success: true; data: WalletBalance }>('/billing/wallet').then((r) => r.data.data)

export const listWalletStatement = (p?: { page?: number; per_page?: number }) =>
  api.get<PaginatedResponse<WalletEntry>>('/billing/statement', { params: p }).then((r) => r.data)

export const topUpWallet = (amount: number, currency: string, gatewayOrderId?: string) =>
  api
    .post<{ success: true; data: WalletBalance }>('/billing/wallet/topup', {
      amount,
      currency,
      gateway_order_id: gatewayOrderId,
    })
    .then((r) => r.data.data)

// ── Platform Gateways (super-admin) ───────────────────────────────────────────

export const listPlatformGateways = () =>
  api
    .get<{ success: true; data: PaymentGatewayConfig[] }>('/billing/platform/gateways')
    .then((r) => r.data.data)

export const createPlatformGateway = (body: {
  gateway: string
  label?: string
  config: Record<string, unknown>
  is_active?: boolean
}) =>
  api
    .post<{ success: true; data: PaymentGatewayConfig }>('/billing/platform/gateways', body)
    .then((r) => r.data.data)

export const updatePlatformGateway = (
  id: string,
  body: { label?: string; config?: Record<string, unknown> },
) =>
  api
    .put<{ success: true; data: PaymentGatewayConfig }>(`/billing/platform/gateways/${id}`, body)
    .then((r) => r.data.data)

export const deletePlatformGateway = (id: string) =>
  api.delete(`/billing/platform/gateways/${id}`).then((r) => r.data)

export const activatePlatformGateway = (id: string) =>
  api.post(`/billing/platform/gateways/${id}/activate`).then((r) => r.data)

// ── Domain Gateways ────────────────────────────────────────────────────────────

export const listDomainGateways = () =>
  api
    .get<{ success: true; data: PaymentGatewayConfig[] }>('/billing/gateways')
    .then((r) => r.data.data)

export const createDomainGateway = (body: {
  gateway: string
  label?: string
  config: Record<string, unknown>
  is_active?: boolean
}) =>
  api
    .post<{ success: true; data: PaymentGatewayConfig }>('/billing/gateways', body)
    .then((r) => r.data.data)

export const updateDomainGateway = (id: string, body: { label?: string; config?: Record<string, unknown> }) =>
  api
    .put<{ success: true; data: PaymentGatewayConfig }>(`/billing/gateways/${id}`, body)
    .then((r) => r.data.data)

export const deleteDomainGateway = (id: string) =>
  api.delete(`/billing/gateways/${id}`).then((r) => r.data)

export const activateDomainGateway = (id: string) =>
  api.post(`/billing/gateways/${id}/activate`).then((r) => r.data)

// ── Billing Profiles ───────────────────────────────────────────────────────────

export const getPlatformProfile = () =>
  api
    .get<{ success: true; data: BillingProfile }>('/billing/platform/profile')
    .then((r) => r.data.data)

export const updatePlatformProfile = (body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>('/billing/platform/profile', body)
    .then((r) => r.data.data)

export const getDomainProfile = () =>
  api
    .get<{ success: true; data: BillingProfile }>('/billing/profile')
    .then((r) => r.data.data)

export const updateDomainProfile = (body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>('/billing/profile', body)
    .then((r) => r.data.data)

export const getCustomerProfile = (userId: string) =>
  api
    .get<{ success: true; data: BillingProfile }>(`/billing/customers/${userId}/profile`)
    .then((r) => r.data.data)

export const updateCustomerProfile = (userId: string, body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>(`/billing/customers/${userId}/profile`, body)
    .then(r => r.data.data)

// ── Super admin: per-domain billing profile ────────────────────────────────────

export const getDomainBillingProfile = (domainId: string) =>
  api
    .get<{ success: true; data: BillingProfile }>(`/domains/${domainId}/billing/profile`)
    .then(r => r.data.data)

export const updateDomainBillingProfile = (domainId: string, body: Partial<BillingProfile>) =>
  api
    .put<{ success: true; data: BillingProfile }>(`/domains/${domainId}/billing/profile`, body)
    .then(r => r.data.data)

// ── Super admin: per-domain subscription management ───────────────────────────

export const getDomainSubscription = (domainId: string) =>
  api
    .get<{ success: true; data: DomainSubscription | DomainSubscription[] }>(`/domains/${domainId}/subscription`)
    .then(r => {
      const d = r.data.data
      return Array.isArray(d) ? d : [d]
    })

export const cancelDomainSubscription = (domainId: string) =>
  api.post(`/domains/${domainId}/subscription/cancel`).then(r => r.data)

export const activateDomainSubscription = (domainId: string, body: { plan_id: string; unit_count: number }) =>
  api
    .post<{ success: true; data: DomainSubscription }>(`/domains/${domainId}/subscription/activate`, body)
    .then(r => r.data.data)

export const changeDomainSubscriptionPlan = (domainId: string, body: { plan_id: string; unit_count?: number }) =>
  api
    .put<{ success: true; data: DomainSubscription }>(`/domains/${domainId}/subscription/plan`, body)
    .then(r => r.data.data)

// ── PDF Templates ──────────────────────────────────────────────────────────────

export interface PdfTemplate {
  type: 'invoice' | 'receipt' | 'statement'
  html_template: string | null
  updated_at: string | null
}

export const getPdfTemplate = (type: string) =>
  api.get<{ success: true; data: PdfTemplate }>(`/billing/pdf-templates/${type}`).then(r => r.data.data)

export const updatePdfTemplate = (type: string, body: { html_template: string }) =>
  api.put<{ success: true; data: PdfTemplate }>(`/billing/pdf-templates/${type}`, body).then(r => r.data.data)

// ── Signing Certificate ────────────────────────────────────────────────────────

export interface SigningCert {
  id: string
  domain_id: string
  subject_cn: string
  org_name: string
  serial: string
  expires_at: string
  expires_at_display: string | null
  is_active: boolean
  uploaded_by: string | null
  created_at: string
  created_at_display: string | null
}

export const getSigningCert = () =>
  api.get<{ success: true; data: SigningCert | null }>('/billing/signing-cert').then(r => r.data.data ?? null)

/** Upload a PFX/PKCS#12 certificate for PDF signing (base64-encoded PFX + optional password) */
export const uploadSigningCert = (body: { pfx_base64: string; password?: string }) =>
  api.post<{ success: true; data: SigningCert }>('/billing/signing-cert', body).then(r => r.data.data)

/** Revoke / delete the active signing certificate */
export const deleteSigningCert = () =>
  api.delete('/billing/signing-cert').then(r => r.data)

// ── Tax Config ─────────────────────────────────────────────────────────────────

export const getTaxConfig = () =>
  api.get<{ success: true; data: TaxConfig }>('/billing/tax-config').then(r => r.data.data)

export const updateTaxConfig = (body: Partial<TaxConfig>) =>
  api.put<{ success: true; data: TaxConfig }>('/billing/tax-config', body).then(r => r.data.data)

export interface GSTLookupResult {
  gst_number: string
  format_valid: boolean
  state_code: string
  trade_name: string
  legal_name: string
  status: string
  verified: boolean
  cached_at: string | null
}

export const lookupGst = (gstNumber: string) =>
  api.get<{ success: true; data: GSTLookupResult }>(`/billing/gst/${gstNumber}`).then(r => r.data.data)

/** Force-refresh GSTIN data from external GST API (bypasses 24-hour cache) */
export const refreshGstLookup = (gstNumber: string) =>
  api.post<{ success: true; data: GSTLookupResult }>(`/billing/gst/${gstNumber}/refresh`).then(r => r.data.data)

// ── GST Exports ────────────────────────────────────────────────────────────────

/** Download GSTR-1 JSON for the given period (format: MMYYYY, e.g. "042026") */
export const downloadGstr1Export = (period: string) =>
  api.get('/billing/exports/gstr1', { params: { period }, responseType: 'blob' }).then(r => r.data as Blob)

/** Download GSTN-compatible Excel (.xlsx) workbook for a date range */
export const downloadGstnExcelExport = (from: string, to: string) =>
  api.get('/billing/exports/gstn-excel', { params: { from, to }, responseType: 'blob' }).then(r => r.data as Blob)

// ── Currencies ─────────────────────────────────────────────────────────────────

export const getCurrencies = () =>
  api.get<{ success: true; data: string[] }>('/billing/currencies').then(r => r.data.data)

export const getPlatformCurrencies = () =>
  api.get<{ success: true; data: string[] }>('/billing/platform/currencies').then(r => r.data.data)

// ── Billing Summary ────────────────────────────────────────────────────────────

export interface BillingSummary {
  active_subscriptions: number
  overdue_invoices: number
  overdue_amount: number
  wallet_balance: number
  currency: string
  next_renewal_at: string | null
}

export interface PlatformBillingSummary {
  active_domain_subscriptions: number
  active_customer_subscriptions: number
  overdue_invoices: number
  overdue_amount: number
}

export const getBillingSummary = () =>
  api.get<{ success: true; data: BillingSummary }>('/billing/summary').then(r => r.data.data)

export const getPlatformBillingSummary = () =>
  api.get<{ success: true; data: PlatformBillingSummary }>('/billing/platform/summary').then(r => r.data.data)

// ── Renewals & Overdue ─────────────────────────────────────────────────────────

export interface UpcomingRenewal {
  customer_user_id: string
  customer_name: string
  plan_name: string
  renews_at: string
  amount: number
  currency: string
}

export interface OverdueInvoice {
  customer_user_id: string
  customer_name: string
  invoice_id: string
  invoice_number: string
  due_date: string
  amount: number
  currency: string
  days_overdue: number
}

export const getUpcomingRenewals = (days = 30) =>
  api.get<{ success: true; data: UpcomingRenewal[] }>('/billing/renewals', { params: { days } }).then(r => r.data.data ?? [])

export const getOverdueInvoices = () =>
  api.get<{ success: true; data: OverdueInvoice[] }>('/billing/overdue').then(r => r.data.data ?? [])

// ── Subscription History ───────────────────────────────────────────────────────

export interface SubscriptionEvent {
  id: string
  subscription_id: string
  subscription_type: 'customer' | 'domain'
  event_type: 'activated' | 'plan_changed' | 'paused' | 'resumed' | 'cancelled' | 'reactivated' | 'invoice_generated' | 'payment_received' | 'suspended' | 'restored'
  description: string
  performed_by: string | null
  meta: Record<string, unknown> | null
  created_at: string
  created_at_display: string | null
}

export const getSubscriptionHistory = (userId: string) =>
  api.get<{ success: true; data: SubscriptionEvent[] }>(`/billing/customers/${userId}/subscription/history`).then(r => r.data.data ?? [])

// ── Owner Self-Service (my/*) ──────────────────────────────────────────────────

export const getMySubscription = () =>
  api
    .get<{ success: true; data: CustomerSubscription[] }>('/billing/my/subscription')
    .then(r => r.data.data ?? [])

export const getMyInvoices = (p?: { page?: number; limit?: number; status?: string }) =>
  api.get<PaginatedResponse<Invoice>>('/billing/my/invoices', { params: p }).then(r => r.data)

export const getMyWallet = () =>
  api.get<{ success: true; data: WalletBalance }>('/billing/my/wallet').then(r => r.data.data)

export const getMyPayments = (p?: { page?: number; limit?: number }) =>
  api.get<PaginatedResponse<Payment>>('/billing/my/payments', { params: p }).then(r => r.data)

// ── Dunning Rules ──────────────────────────────────────────────────────────────

export interface DunningRule {
  id: string
  days_after_due: number
  channel: 'email' | 'whatsapp' | 'sms' | 'push'
  is_active: boolean
  created_at: string
}

export const getDunningRules = () =>
  api.get<{ success: true; data: DunningRule[] }>('/billing/dunning-rules').then(r => r.data.data ?? [])

export const createDunningRule = (body: { days_after_due: number; channel: DunningRule['channel']; is_active?: boolean }) =>
  api.post<{ success: true; data: DunningRule }>('/billing/dunning-rules', body).then(r => r.data.data)

export const deleteDunningRule = (id: string) =>
  api.delete(`/billing/dunning-rules/${id}`).then(r => r.data)

// ── Plan Comparison ────────────────────────────────────────────────────────────

export interface ComparePlan extends BillingPlan {
  is_recommended: boolean
  annual_equivalent: number
  savings_vs_monthly: number | null
}

export const comparePlans = () =>
  api.get<{ success: true; data: ComparePlan[] }>('/billing/plans/compare').then(r => r.data.data ?? [])
